import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import StatusPill from "@/components/StatusPill";
import InvoicePreview, { computeStatus } from "@/components/InvoicePreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { endpoints } from "@/lib/api";
import { fmtMoney, fmtDate, todayISO } from "@/lib/utils-format";
import { downloadInvoicePDF, printInvoice } from "@/lib/invoice-pdf";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Download, Send, CheckCircle2, Eye, Printer, X, User, FileText,
} from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";

const STATUSES = ["Draft", "Sent", "Paid", "Overdue"];
const METHODS = ["Cash", "Zelle", "Check", "Credit/Debit Card", "ACH Transfer"];
const DEFAULT_TAX_RATE = 8.25;

const newLineItemId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function blankForm() {
  const today = todayISO();
  return {
    number: "",
    client_id: "", client_name: "", address: "", phone: "", email: "",
    service: "", crew: "", customer_notes: "",
    status: "Draft", payment_method: "",
    line_items: [{ _lid: newLineItemId(), description: "", qty: 1, unit_price: 0, amount: 0 }],
    subtotal: 0, tax_rate: DEFAULT_TAX_RATE, tax: 0, discount: 0, deposit: 0,
    total: 0, balance: 0,
    issue_date: today, service_date: today,
    due_date: todayISO(15),
    paid_date: "", notes: "",
  };
}

function recalc(f) {
  const subtotal = f.line_items.reduce(
    (s, li) => s + Number(li.qty || 0) * Number(li.unit_price || 0), 0
  );
  const tax = +(subtotal * (Number(f.tax_rate || 0) / 100)).toFixed(2);
  const discount = Number(f.discount || 0);
  const deposit = Number(f.deposit || 0);
  const total = +(subtotal + tax - discount).toFixed(2);
  const rawBalance = +(total - deposit).toFixed(2);
  const balance = rawBalance < 0 ? 0 : rawBalance;
  return { ...f, subtotal, tax, total, balance };
}

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [previewing, setPreviewing] = useState(null);
  const [mode, setMode] = useState("existing"); // 'existing' | 'manual'
  const [search, setSearch] = useSearchParams();
  const previewRef = useRef(null);

  const load = useCallback(async () => {
    const [inv, cli, jb] = await Promise.all([
      endpoints.list("invoices"),
      endpoints.list("clients"),
      endpoints.list("jobs"),
    ]);
    setItems(inv.data); setClients(cli.data); setJobs(jb.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  // --- Auto-fill from client or completed job ---
  const buildFromClient = useCallback((c, base) => {
    if (!c) return base;
    return {
      ...base,
      client_id: c.id,
      client_name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
      address: [c.street, c.city, c.state, c.zip].filter(Boolean).join(", "),
      phone: c.phone || "",
      email: c.email || "",
      customer_notes: c.customer_notes || "",
    };
  }, []);

  const buildFromJob = useCallback((j, c, base) => {
    if (!j) return base;
    const fromClient = buildFromClient(c, base);
    const price = Number(j.price || 0);
    const next = {
      ...fromClient,
      service: j.service || "",
      crew: j.crew || "",
      service_date: j.date || base.service_date,
      line_items: [{
        _lid: newLineItemId(),
        description: j.service || "Landscaping Service",
        qty: 1,
        unit_price: price,
        amount: price,
      }],
      notes: j.notes || fromClient.notes || "",
    };
    return recalc(next);
  }, [buildFromClient]);

  // Handle ?new=1[&client=ID|&job=ID]
  useEffect(() => {
    if (search.get("new") !== "1") return;
    const jobId = search.get("job");
    const clientId = search.get("client");
    if (jobId && jobs.length === 0) return;
    if (clientId && clients.length === 0) return;

    (async () => {
      const next = blankForm();
      try {
        const r = await endpoints.nextInvoiceNumber();
        next.number = r.data.number;
      } catch {
        next.number = `SC-${1001 + items.length}`;
      }

      let prepared = next;
      if (jobId) {
        const j = jobs.find((x) => x.id === jobId);
        const c = j ? clients.find((x) => x.id === j.client_id) : null;
        prepared = buildFromJob(j, c, next);
      } else if (clientId) {
        const c = clients.find((x) => x.id === clientId);
        prepared = buildFromClient(c, next);
      }
      setEditing(null);
      setMode("existing");
      setForm(prepared);
      setOpen(true);

      const sp = new URLSearchParams(search);
      sp.delete("new"); sp.delete("client"); sp.delete("job");
      setSearch(sp, { replace: true });
    })();
  }, [search, clients, jobs]);

  const updateItem = (idx, k, v) => {
    const li = [...form.line_items];
    li[idx] = { ...li[idx], [k]: k === "description" ? v : Number(v || 0) };
    li[idx].amount = +(li[idx].qty * li[idx].unit_price).toFixed(2);
    setForm(recalc({ ...form, line_items: li }));
  };
  const addLine = () =>
    setForm({ ...form, line_items: [...form.line_items, { _lid: newLineItemId(), description: "", qty: 1, unit_price: 0, amount: 0 }] });
  const removeLine = (i) =>
    setForm(recalc({ ...form, line_items: form.line_items.filter((_, idx) => idx !== i) }));

  const onClientChange = (id) => {
    const c = clients.find((x) => x.id === id);
    setForm(buildFromClient(c, form));
  };

  const onJobChange = (id) => {
    const j = jobs.find((x) => x.id === id);
    const c = j ? clients.find((x) => x.id === j.client_id) : null;
    setForm(buildFromJob(j, c, form));
  };

  const completedJobs = useMemo(
    () => jobs.filter((j) => j.status === "Completed"),
    [jobs]
  );

  const startNew = async () => {
    const next = blankForm();
    try {
      const r = await endpoints.nextInvoiceNumber();
      next.number = r.data.number;
    } catch {
      next.number = `SC-${1001 + items.length}`;
    }
    setEditing(null);
    setMode("existing");
    setForm(next);
    setOpen(true);
  };

  const startNewManual = async () => {
    const next = blankForm();
    try {
      const r = await endpoints.nextInvoiceNumber();
      next.number = r.data.number;
    } catch {
      next.number = `SC-${1001 + items.length}`;
    }
    setEditing(null);
    setMode("manual");
    setForm(next);
    setOpen(true);
  };

  const startEdit = (i) => {
    setEditing(i);
    setMode(i.client_id ? "existing" : "manual");
    setForm(recalc({ ...blankForm(), ...i }));
    setOpen(true);
  };

  const togglePaidInFull = (checked) => {
    if (checked) {
      const total = Number(form.total || 0);
      setForm(recalc({
        ...form,
        deposit: total,
        status: "Paid",
        payment_method: form.payment_method || "Cash",
        paid_date: form.paid_date || todayISO(),
      }));
    } else {
      setForm(recalc({ ...form, deposit: 0, status: "Draft", paid_date: "" }));
    }
  };

  const save = async () => {
    if (mode === "existing" && !form.client_id) { toast.error("Please select a customer or switch to Manual"); return; }
    if (mode === "manual" && !form.client_name?.trim()) { toast.error("Please enter a customer name"); return; }
    const data = recalc(form);
    if (editing) await endpoints.update("invoices", editing.id, data);
    else await endpoints.create("invoices", data);
    toast.success("Invoice saved");
    setOpen(false); load();
  };

  const markPaid = async (i) => {
    const method = i.payment_method || "Cash";
    const today = todayISO();
    const updated = { ...i, status: "Paid", payment_method: method, deposit: i.total, balance: 0, paid_date: today };
    await endpoints.update("invoices", i.id, updated);
    await endpoints.create("payments", {
      client_id: i.client_id, client_name: i.client_name,
      invoice_number: i.number, amount: i.total,
      method,
      date: today,
      status: "Received", notes: "",
    });
    toast.success("Marked as paid + payment recorded");
    load();
  };

  const send = async (i) => { await endpoints.update("invoices", i.id, { ...i, status: "Sent" }); toast.success("Invoice sent"); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this invoice?")) return; await endpoints.remove("invoices", id); load(); };

  const handleDownload = async () => {
    if (!previewRef.current) return;
    try {
      await downloadInvoicePDF(previewRef.current, `Invoice-${previewing?.number || "doc"}.pdf`);
    } catch {
      toast.error("Could not generate PDF");
    }
  };

  return (
    <Layout title="Invoices" subtitle={`${items.length} invoices · ${items.filter((i) => i.status === "Paid").length} paid`}>
      <div className="flex justify-end gap-2 mb-4">
        <Button variant="outline" onClick={startNewManual} data-testid="invoices-new-manual">
          <FileText className="size-4 mr-1.5"/>Manual Invoice
        </Button>
        <Button onClick={startNew} data-testid="invoices-new">
          <Plus className="size-4 mr-1.5"/>New Invoice
        </Button>
      </div>

      <Card className="rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-muted/40">
              <tr className="text-xs uppercase tracking-wider text-muted-foreground text-left">
                <th className="p-4 font-semibold">Number</th>
                <th className="p-4 font-semibold">Customer</th>
                <th className="p-4 font-semibold">Service</th>
                <th className="p-4 font-semibold">Issued</th>
                <th className="p-4 font-semibold">Total</th>
                <th className="p-4 font-semibold">Balance</th>
                <th className="p-4 font-semibold">Status</th>
                <th className="p-4 font-semibold w-48">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="8" className="p-10 text-center text-muted-foreground">No invoices yet.</td></tr>}
              {items.map((i) => {
                const { isPaid, balance, credit } = computeStatus(i);
                const label = credit > 0 ? `Credit ${fmtMoney(credit)}` : fmtMoney(balance);
                let labelColor = "";
                if (credit > 0) labelColor = "text-secondary";
                else if (!isPaid && balance > 0) labelColor = "text-destructive";
                return (
                  <tr key={i.id} className="border-t border-border hover:bg-muted/30">
                    <td className="p-4 font-mono text-xs font-semibold">{i.number}</td>
                    <td className="p-4">{i.client_name}</td>
                    <td className="p-4">{i.service}</td>
                    <td className="p-4 text-xs">{fmtDate(i.issue_date)}</td>
                    <td className="p-4 font-mono font-semibold">{fmtMoney(i.total)}</td>
                    <td className={`p-4 font-mono ${labelColor}`}>{label}</td>
                    <td className="p-4"><StatusPill status={i.status}/></td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="ghost" className="size-7" onClick={() => setPreviewing(i)} title="Preview" data-testid={`invoice-preview-${i.number}`}><Eye className="size-3.5"/></Button>
                        <Button size="icon" variant="ghost" className="size-7" onClick={() => startEdit(i)}><Pencil className="size-3.5"/></Button>
                        {i.status === "Draft" && <Button size="icon" variant="ghost" className="size-7" onClick={() => send(i)}><Send className="size-3.5"/></Button>}
                        {i.status !== "Paid" && <Button size="icon" variant="ghost" className="size-7 text-secondary" onClick={() => markPaid(i)} title="Mark Paid"><CheckCircle2 className="size-3.5"/></Button>}
                        <Button size="icon" variant="ghost" className="size-7 text-destructive" onClick={() => remove(i.id)}><Trash2 className="size-3.5"/></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Invoice Editor */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="invoice-form">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing ? `Edit Invoice ${editing.number || ""}` : `New Invoice ${form.number ? `· ${form.number}` : ""}`}
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-2 mb-1">
            <Button
              size="sm"
              variant={mode === "existing" ? "default" : "outline"}
              onClick={() => setMode("existing")}
              data-testid="invoice-mode-existing"
            ><User className="size-4 mr-1.5"/>Existing Client / Job</Button>
            <Button
              size="sm"
              variant={mode === "manual" ? "default" : "outline"}
              onClick={() => setMode("manual")}
              data-testid="invoice-mode-manual"
            ><FileText className="size-4 mr-1.5"/>Manual / One-Time Invoice</Button>
          </div>

          <div className="text-xs text-muted-foreground mb-2">
            {mode === "existing"
              ? "Select a client or a completed job to auto-fill everything."
              : "Enter customer details below — no saved client needed."}
          </div>

          {mode === "existing" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Client</Label>
                <Select value={form.client_id} onValueChange={onClientChange}>
                  <SelectTrigger data-testid="invoice-client"><SelectValue placeholder="Select existing client"/></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Completed Job (optional)</Label>
                <Select value="" onValueChange={onJobChange}>
                  <SelectTrigger data-testid="invoice-job"><SelectValue placeholder="Pick a completed job"/></SelectTrigger>
                  <SelectContent>
                    {completedJobs.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No completed jobs</div>}
                    {completedJobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.client_name} · {j.service} · {fmtDate(j.date)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5 sm:col-span-2 grid sm:grid-cols-2 gap-4">
                <div><Label className="text-xs">Customer</Label><div className="px-3 py-2 rounded-md bg-muted/50 text-sm h-9 truncate">{form.client_name || "—"}</div></div>
                <div><Label className="text-xs">Address</Label><div className="px-3 py-2 rounded-md bg-muted/50 text-sm h-9 truncate">{form.address || "—"}</div></div>
                <div><Label className="text-xs">Phone</Label><div className="px-3 py-2 rounded-md bg-muted/50 text-sm h-9">{form.phone || "—"}</div></div>
                <div><Label className="text-xs">Email</Label><div className="px-3 py-2 rounded-md bg-muted/50 text-sm h-9 truncate">{form.email || "—"}</div></div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="invoice-manual-fields">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Customer Name *</Label>
                <Input value={form.client_name} onChange={(e)=>setForm({...form, client_name:e.target.value, client_id:""})} placeholder="John Doe" data-testid="manual-customer-name"/>
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} placeholder="(512) 555-0123" data-testid="manual-phone"/>
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} placeholder="customer@example.com" data-testid="manual-email"/>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Address</Label>
                <Input value={form.address} onChange={(e)=>setForm({...form, address:e.target.value})} placeholder="1234 Main St, Austin, TX 78701" data-testid="manual-address"/>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div className="space-y-1.5"><Label>Service</Label><Input value={form.service} onChange={(e)=>setForm({...form, service:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Crew</Label><Input value={form.crew} onChange={(e)=>setForm({...form, crew:e.target.value})} placeholder="Crew A / Crew B / Solo"/></div>
            <div className="space-y-1.5"><Label>Invoice Date</Label><Input type="date" value={form.issue_date} onChange={(e)=>setForm({...form, issue_date:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Service Date</Label><Input type="date" value={form.service_date} onChange={(e)=>setForm({...form, service_date:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v)=>setForm({...form, status:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Payment Method</Label>
              <Select value={form.payment_method||""} onValueChange={(v)=>setForm({...form, payment_method:v})}>
                <SelectTrigger><SelectValue placeholder="None yet"/></SelectTrigger>
                <SelectContent>{METHODS.map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Line Items */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Line Items</Label>
              <Button size="sm" variant="outline" onClick={addLine}><Plus className="size-3.5 mr-1"/>Add Line</Button>
            </div>
            {form.line_items.map((li, i) => (
              <div key={li._lid || `idx-${i}`} className="grid grid-cols-12 gap-2 items-center mt-2" data-testid={`invoice-line-${i}`}>
                <Input className="col-span-12 sm:col-span-6" placeholder="Description" value={li.description} onChange={(e)=>updateItem(i, "description", e.target.value)}/>
                <Input className="col-span-3 sm:col-span-1" type="number" value={li.qty} onChange={(e)=>updateItem(i, "qty", e.target.value)}/>
                <Input className="col-span-4 sm:col-span-2" type="number" step="0.01" value={li.unit_price} onChange={(e)=>updateItem(i, "unit_price", e.target.value)}/>
                <div className="col-span-4 sm:col-span-2 font-mono text-sm text-right">{fmtMoney(li.amount)}</div>
                <Button size="icon" variant="ghost" className="col-span-1 size-8 text-destructive" onClick={()=>removeLine(i)}><Trash2 className="size-3.5"/></Button>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <div className="space-y-1.5"><Label>Tax Rate (%)</Label><Input type="number" step="0.01" value={form.tax_rate} onChange={(e)=>setForm(recalc({...form, tax_rate:Number(e.target.value)}))}/></div>
            <div className="space-y-1.5"><Label>Discount ($)</Label><Input type="number" step="0.01" value={form.discount} onChange={(e)=>setForm(recalc({...form, discount:Number(e.target.value)}))}/></div>
            <div className="space-y-1.5"><Label>Deposit ($)</Label><Input type="number" step="0.01" value={form.deposit} onChange={(e)=>setForm(recalc({...form, deposit:Number(e.target.value)}))}/></div>
            <div className="sm:col-span-3 bg-muted/30 rounded-lg p-4 grid sm:grid-cols-5 gap-3 text-xs">
              <Stat label="Subtotal" value={fmtMoney(form.subtotal)}/>
              <Stat label="Tax" value={fmtMoney(form.tax)}/>
              <Stat label="Discount" value={`-${fmtMoney(form.discount||0)}`}/>
              <Stat label="Deposit" value={`-${fmtMoney(form.deposit||0)}`}/>
              <div className="font-display font-bold text-base">
                {Number(form.deposit||0) > Number(form.total||0)
                  ? <>Credit: <span className="text-secondary">{fmtMoney(Number(form.deposit||0) - Number(form.total||0))}</span></>
                  : <>Balance: {fmtMoney(form.balance)}</>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-3 p-3 bg-secondary/10 border border-secondary/30 rounded-lg" data-testid="paid-in-full-row">
            <Checkbox
              id="paid-in-full"
              checked={Number(form.deposit || 0) >= Number(form.total || 0) && Number(form.total || 0) > 0}
              onCheckedChange={togglePaidInFull}
              data-testid="paid-in-full-checkbox"
            />
            <Label htmlFor="paid-in-full" className="cursor-pointer text-sm font-semibold">
              Mark as Paid in Full
            </Label>
            <span className="text-xs text-muted-foreground ml-auto">
              Sets deposit to total, marks invoice as Paid, and stamps the invoice.
            </span>
          </div>

          <div className="space-y-1.5 mt-3"><Label>Customer Notes</Label><Textarea rows={2} value={form.customer_notes||""} onChange={(e)=>setForm({...form, customer_notes:e.target.value})}/></div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="invoice-save">Save Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview / Print / Download dialog — uses the SAME InvoicePreview component */}
      <Dialog open={!!previewing} onOpenChange={(o)=>!o && setPreviewing(null)}>
        <DialogContent className="max-w-4xl max-h-[95vh] p-0 overflow-hidden bg-muted/30 no-print [&>button.absolute]:hidden">
          {previewing && (
            <>
              <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background">
                <DialogTitle className="font-display text-lg">Invoice {previewing.number}</DialogTitle>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={printInvoice} data-testid="invoice-print"><Printer className="size-4 mr-1.5"/>Print</Button>
                  <Button size="sm" onClick={handleDownload} data-testid="invoice-download"><Download className="size-4 mr-1.5"/>Download PDF</Button>
                  <Button size="icon" variant="ghost" className="size-8" onClick={()=>setPreviewing(null)}><X className="size-4"/></Button>
                </div>
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: "calc(95vh - 60px)" }}>
                <div className="py-6 px-4 flex justify-center">
                  <InvoicePreview ref={previewRef} invoice={previewing} />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Body-level print portal — same component, hidden on screen, visible only on print */}
      {previewing && createPortal(
        <div className="invoice-print-host" aria-hidden="true">
          <InvoicePreview invoice={previewing} />
        </div>,
        document.body
      )}
    </Layout>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-muted-foreground uppercase tracking-wider text-[10px] font-semibold">{label}</div>
      <div className="text-foreground font-semibold font-mono">{value}</div>
    </div>
  );
}
